import { Injectable } from '@angular/core';
import type { ModelContext } from '@mcp-b/webmcp-types';
import {
  APPLY_AUTOMATION_CHANGE_TOOL,
  APPLY_INBOX_FILTERS_TOOL,
  APPLY_KB_ARTICLE_DIFF_TOOL,
  ApplyAutomationChangeInput,
  ApplyKbArticleDiffInput,
  CREATE_KB_ARTICLE_DRAFT_TOOL,
  CreateKbArticleDraftInput,
  INSERT_DRAFT_RESPONSE_TOOL,
  InsertDraftResponseInput,
  INSERT_TEXT_AT_CURSOR_TOOL,
  InsertTextAtCursorInput,
  NAVIGATE_ACTION_NAME,
  REPLACE_SELECTED_TEXT_TOOL,
  ReplaceSelectedTextInput,
} from '@unthread-io/copilot-frontend-tools';
import { WhereClause } from 'src/models';

import { AnalyticsService } from './analytics.service';
import { CopilotContextPayload, CopilotContextService } from './copilot-context.service';
import { FilterAssistantService } from './filter-assistant.service';
import { NavigationAssistantService } from './navigation-assistant.service';

/**
 * Registers Unthread frontend capabilities as `navigator.modelContext` tools
 * so staff can drive the app from their own MCP client (Claude Desktop,
 * Cursor, native browser agents) — in addition to the in-app Copilot.
 *
 * The polyfill (`@mcp-b/global`) is imported for side effects in `main.ts`
 * and sets up `navigator.modelContext` before this service runs.
 */
@Injectable({
  providedIn: 'root',
})
export class WebMcpService {
  private static readonly GET_STARTED_TOOL_NAME = 'unthread_get_started';
  private static readonly NAVIGATION_ASSISTANT_TOOL_NAME = 'navigation_assistant';
  private static readonly FILTER_ASSISTANT_TOOL_NAME = 'filter_assistant';

  private static readonly TOOL_NAMES = [
    WebMcpService.GET_STARTED_TOOL_NAME,
    'unthread_get_page_context',
    INSERT_DRAFT_RESPONSE_TOOL.name,
    APPLY_AUTOMATION_CHANGE_TOOL.name,
    REPLACE_SELECTED_TEXT_TOOL.name,
    INSERT_TEXT_AT_CURSOR_TOOL.name,
    CREATE_KB_ARTICLE_DRAFT_TOOL.name,
    APPLY_KB_ARTICLE_DIFF_TOOL.name,
    WebMcpService.NAVIGATION_ASSISTANT_TOOL_NAME,
    WebMcpService.FILTER_ASSISTANT_TOOL_NAME,
  ] as const;

  private static readonly DOM_HINT_ELEMENT_ID = 'unthread-webmcp-hint';

  constructor(
    private copilotContext: CopilotContextService,
    private analytics: AnalyticsService,
    private navigationAssistant: NavigationAssistantService,
    private filterAssistant: FilterAssistantService,
  ) {}

  register(): void {
    // Bridge extensions (e.g. MCPCat WebMCP Bridge) attach their toolschanged
    // listener asynchronously after their content scripts log "loaded". The
    // initial registerTool broadcast can fire before that listener is live and
    // get silently dropped. Re-broadcast on a small schedule so the bridge
    // catches us once it has fully attached.
    const onReady = () => {
      this.registerTools();

      [500, 1500, 3000].forEach((delay) => {
        setTimeout(() => this.rebroadcast(), delay);
      });
    };

    if (document.readyState === 'complete') {
      queueMicrotask(onReady);
    } else {
      window.addEventListener('load', onReady, { once: true });
    }

    // Install discoverability hooks. The window stub is for agents that probe
    // global JS state first; the DOM hint is for agents that scrape page
    // content. Both point at the canonical `navigator.modelContext` surface.
    this.installWindowStub();
    if (document.readyState !== 'loading') {
      this.installAgentHint();
    } else {
      document.addEventListener('DOMContentLoaded', () => this.installAgentHint(), { once: true });
    }
  }

  /**
   * Expose a small stub at `window.webmcp` so in-page AI agents that probe
   * the global object first (a natural-feeling but non-spec location) don't
   * bail before checking `navigator.modelContext`. The stub proxies the
   * tool-list and tool-call surface, so an agent can discover and invoke
   * tools through the stub without redirecting.
   *
   * Non-aggressive: skipped if `window.webmcp` already exists (e.g. if the
   * spec ever standardizes the location, or if a future browser feature
   * assigns it). Configurable so it can be cleanly overwritten.
   */
  private installWindowStub(): void {
    const win = window as unknown as Record<string, unknown>;
    if (win['webmcp']) {
      return;
    }

    Object.defineProperty(window, 'webmcp', {
      value: {
        api: 'navigator.modelContext',
        spec: 'https://webmachinelearning.github.io/webmcp/',
        hint: 'WebMCP tools live at navigator.modelContext. Call listTools() to discover or callTool({ name, arguments }) to invoke. Result text is JSON-encoded in result.content[0].text.',
        listTools: () => {
          const ctx = navigator.modelContext as { listTools?: () => unknown[] } | undefined;
          return ctx?.listTools?.() ?? [];
        },
        callTool: (params: { name: string; arguments?: Record<string, unknown> }) => {
          const ctx = navigator.modelContext as { callTool?: (p: typeof params) => unknown } | undefined;
          return ctx?.callTool?.(params);
        },
      },
      writable: true,
      configurable: true,
    });
  }

  /**
   * Inject a hidden DOM element pointing in-page AI agents (Claude for Chrome,
   * Atlas, etc.) at `navigator.modelContext`. Most in-page agents don't yet
   * auto-discover WebMCP tools, but they do scrape the page DOM for context.
   * This hint lets a user trigger discovery with one keyword ("webmcp", "mcp",
   * "page tools") instead of pasting a multi-paragraph priming prompt.
   *
   * Bare form by design — links the agent to `navigator.modelContext` and
   * documents the calling convention, but does NOT enumerate the tool catalog.
   * The agent calls `listTools()` for the live catalog. Keeps the hint stable
   * as the catalog grows and matches how external MCP clients discover tools.
   */
  private installAgentHint(): void {
    if (document.getElementById(WebMcpService.DOM_HINT_ELEMENT_ID)) {
      return;
    }

    const hint = document.createElement('aside');
    hint.id = WebMcpService.DOM_HINT_ELEMENT_ID;
    hint.setAttribute('aria-hidden', 'true');
    // Visually hidden but present in the DOM so AI agents reading via DOM
    // scrape can find it. Not exposed to assistive tech (aria-hidden) and not
    // visible to sighted users.
    hint.style.cssText = 'position:absolute;left:-9999px;top:auto;width:1px;height:1px;overflow:hidden;';
    hint.innerHTML = [
      '<h2>WebMCP — Unthread tools for AI agents (mcp, webmcp, navigator.modelContext, page tools)</h2>',
      '<p>This page exposes structured tools to in-page AI agents via the W3C WebMCP API at <code>navigator.modelContext</code>. Prefer these tools over DOM scraping when answering questions about Unthread data or driving the dashboard.</p>',
      '<p>Before invoking any other tool, call <code>unthread_get_started</code> — it returns the calling convention, the current capability map, and how this surface pairs with other Unthread MCP surfaces:</p>',
      "<pre>const r = await navigator.modelContext.callTool({ name: 'unthread_get_started' });\nconst guidance = JSON.parse(r.content[0].text);</pre>",
      '<p>Then list the full tool catalog as needed:</p>',
      '<pre>const tools = await navigator.modelContext.listTools();</pre>',
    ].join('');
    document.body.appendChild(hint);
  }

  /**
   * Build the orientation payload returned by `unthread_get_started`.
   *
   * Same body for both audiences — bridge clients arrive here via the
   * tool's "call this FIRST" description; in-page agents arrive via the
   * DOM hint that names the tool. Keeping orientation prose in one place
   * means we don't have to update two surfaces when capabilities change.
   */
  private buildGetStartedPayload(): Record<string, unknown> {
    return {
      callingConvention: {
        invoke: "navigator.modelContext.callTool({ name, arguments: { /* per the tool's inputSchema */ } })",
        argShape:
          'Single object with `name` and `arguments`. Passing positional args fails with "Tool not found: undefined".',
        parseResult: 'JSON.parse(result.content[0].text) — tool outputs are JSON-encoded strings.',
        discovery: 'navigator.modelContext.listTools() — call for the live catalog.',
      },
      capabilities: {
        thisSurface: {
          name: 'WebMCP (navigator.modelContext)',
          scope: 'Screen-use — drive the Unthread dashboard the staff member is currently viewing.',
          tools: [
            'unthread_get_page_context — snapshot of what the staff member is viewing right now',
            'filter_assistant — natural-language inbox filtering; resolves entities + builds filter clauses and applies',
            "insert_draft_response — insert HTML into the active conversation's reply composer",
            'apply_automation_change — replace the automation currently being edited with a new document',
            'replace_selected_text — replace the current KB-article editor selection with rewritten text',
            'insert_text_at_cursor — insert Markdown at the current KB-article editor cursor as a reviewable diff',
            'create_kb_article_draft — open a freshly-drafted KB article in the editor (app-wide)',
            'apply_kb_article_diff — render a reviewable diff in the KB-article editor for the staff member to accept or reject',
            'navigation_assistant — natural-language navigation; resolves entity names + page catalog into a URL and applies it',
          ],
        },
        pairedSurface: {
          name: 'Unthread API MCP (/api/mcp)',
          scope:
            'Catalog / REST — read and mutate Unthread entities (conversations, projects, KB) outside the current view.',
          note: 'A separate MCP server. The staff member configures it in their MCP client to get entity lookups, CRUD, and KB search. Recommended for any work that needs data not visible on screen.',
          examples: [
            'list_conversations, list_projects, list_ticket_types',
            'get_conversation_by_id, get_current_user, get_project',
            'update_conversation, create_conversation_comment',
            'search_knowledge_base, query_knowledge_base',
          ],
        },
      },
      suggestedWorkflows: [
        "Resolve current conversation: call unthread_get_page_context to read the conversationId, then /api/mcp's update_conversation to apply changes.",
        'Draft a reply: call insert_draft_response with HTML; the composer is left in draft state for the staff member to review and send.',
        "Filter the inbox: call filter_assistant with the user's NL instruction. The assistant resolves entities, builds the filter clauses, and applies — one tool call from your side.",
      ],
      auth: "WebMCP runs inside the staff member's authenticated browser session — each sub-agent call authenticates automatically using the staff member's existing credentials. No API tokens or service accounts needed for this surface.",
    };
  }

  /**
   * Force a fresh WEBMCP_TOOLS_UPDATED broadcast by toggling registration.
   * Used to recover from bridge-listener-attach race conditions.
   */
  private rebroadcast(): void {
    const ctx = navigator.modelContext as ModelContext | undefined;
    if (!ctx) {
      return;
    }

    const registered = (ctx as { listTools?: () => Array<{ name: string }> }).listTools?.() ?? [];
    const knownNames = WebMcpService.TOOL_NAMES.filter((name) => registered.some((t) => t.name === name));
    if (knownNames.length === 0) {
      return;
    }

    knownNames.forEach((name) => ctx.unregisterTool(name));
    this.registerTools();
  }

  /**
   * Pre-validate that the staff member's current view includes the page this
   * tool requires. Throws a useful error if not — the throw becomes the
   * WebMCP error response, letting the caller LLM see what went wrong
   * instead of receiving a false success. Returns the typed page context.
   *
   * Uses `getActiveContextFor` (any currently-rendered provider matching)
   * rather than `getContext` (primary only) so sidebar URLs work: on
   * `/inbox/me/view/<id>` both `inbox` and `conversation-details` are
   * visible, and `requirePage('inbox')` succeeds even though
   * `conversation-details` is the primary page.
   */
  private requirePage<
    T extends 'inbox' | 'conversation-details' | 'automation-edit' | 'knowledge-article-edit' | 'analytics',
  >(expectedPageId: T, toolName: string): Extract<CopilotContextPayload, { pageId: T }> {
    const context = this.copilotContext.getActiveContextFor(expectedPageId);

    if (!context) {
      const activeIds = [...this.copilotContext.activePageIds];
      const actual =
        activeIds.length > 0 ? activeIds.map((id) => `"${id}"`).join(', ') : 'a page with no registered context';
      throw new Error(
        `${toolName} requires the "${expectedPageId}" page to be visible. ` +
          `Currently active: ${actual}. ` +
          `Use navigation_assistant to take them there first, then retry.`,
      );
    }

    return context;
  }

  private registerTools(): void {
    const ctx = navigator.modelContext as ModelContext | undefined;

    if (!ctx) {
      console.warn('[WebMCP] navigator.modelContext unavailable; skipping tool registration');

      return;
    }

    // Orientation tool. Returns the calling convention, the current
    // capability map, and how this WebMCP surface pairs with /api/mcp.
    // Both bridge clients and in-page agents are directed here first —
    // bridge clients via the tool's description, in-page agents via the
    // DOM hint that points them at this tool by name.
    ctx.registerTool({
      name: WebMcpService.GET_STARTED_TOOL_NAME,
      description:
        'REQUIRED — call this FIRST in any session before invoking any other Unthread tool. Returns the calling convention, the current capability map for this WebMCP surface (screen-use tools), and how it pairs with the broader Unthread API server at /api/mcp.',
      inputSchema: { type: 'object', properties: {}, required: [] },
      annotations: { readOnlyHint: true },
      execute: this.instrument(WebMcpService.GET_STARTED_TOOL_NAME, async () => {
        return {
          content: [{ type: 'text', text: JSON.stringify(this.buildGetStartedPayload()) }],
        };
      }),
    });

    ctx.registerTool({
      name: 'unthread_get_page_context',
      description:
        'Snapshot what the staff member is currently viewing in Unthread. Returns the primary page context in `context` (the deepest tag in the route tree) AND the full set of visible pages in `activeContexts` — usually one entry, but two on sidebar URLs like `/inbox/me/view/<id>` where the inbox list and a conversation detail are both visible. Each entry is discriminated by `pageId` ("conversation-details", "inbox", "automation-edit", "knowledge-article-edit", "analytics", or generic). When acting on a non-primary page (e.g. applying filters while a conversation is also open), use the matching entry in `activeContexts`. Returns `context: null, activeContexts: []` on transient/untagged routes.',
      inputSchema: { type: 'object', properties: {}, required: [] },
      annotations: { readOnlyHint: true },
      execute: this.instrument('unthread_get_page_context', async () => {
        const context = this.copilotContext.getContext();
        const activeContexts = this.copilotContext.getAllActiveContexts();

        return {
          content: [
            {
              type: 'text',
              text: JSON.stringify({ url: window.location.href, context, activeContexts }),
            },
          ],
        };
      }),
    });

    // Frontend-mutating tools. The execute body dispatches through CopilotContextService,
    // which is also the dispatch path for the in-app Copilot's BE→FE invocations —
    // so external WebMCP calls and server-emitted invocations land on the same handler.
    // Schemas in @unthread-io/copilot-frontend-tools are typed loosely to avoid a hard
    // dependency on @mcp-b/webmcp-types from the shared module; cast at the call site.
    ctx.registerTool({
      name: INSERT_DRAFT_RESPONSE_TOOL.name,
      description: INSERT_DRAFT_RESPONSE_TOOL.description,
      inputSchema: INSERT_DRAFT_RESPONSE_TOOL.inputSchema as never,
      execute: this.instrument(INSERT_DRAFT_RESPONSE_TOOL.name, async (args) => {
        this.requirePage('conversation-details', INSERT_DRAFT_RESPONSE_TOOL.name);
        const input = args as unknown as InsertDraftResponseInput;
        const inserted = this.copilotContext.executeAction(INSERT_DRAFT_RESPONSE_TOOL.name, input);
        if (!inserted) {
          throw new Error(
            `${INSERT_DRAFT_RESPONSE_TOOL.name}: the conversation-details handler couldn't apply this — the reply composer may not be ready yet, or the html payload was empty.`,
          );
        }

        return { content: [{ type: 'text', text: JSON.stringify({ inserted: true }) }] };
      }),
    });

    ctx.registerTool({
      name: APPLY_AUTOMATION_CHANGE_TOOL.name,
      description: APPLY_AUTOMATION_CHANGE_TOOL.description,
      inputSchema: APPLY_AUTOMATION_CHANGE_TOOL.inputSchema as never,
      execute: this.instrument(APPLY_AUTOMATION_CHANGE_TOOL.name, async (args) => {
        this.requirePage('automation-edit', APPLY_AUTOMATION_CHANGE_TOOL.name);
        const input = args as unknown as ApplyAutomationChangeInput;
        const applied = this.copilotContext.executeAction(APPLY_AUTOMATION_CHANGE_TOOL.name, input);
        if (!applied) {
          throw new Error(
            `${APPLY_AUTOMATION_CHANGE_TOOL.name}: the automation-edit handler couldn't apply this — the automationData payload was empty.`,
          );
        }

        return { content: [{ type: 'text', text: JSON.stringify({ applied: true }) }] };
      }),
    });

    ctx.registerTool({
      name: REPLACE_SELECTED_TEXT_TOOL.name,
      description: REPLACE_SELECTED_TEXT_TOOL.description,
      inputSchema: REPLACE_SELECTED_TEXT_TOOL.inputSchema as never,
      execute: this.instrument(REPLACE_SELECTED_TEXT_TOOL.name, async (args) => {
        this.requirePage('knowledge-article-edit', REPLACE_SELECTED_TEXT_TOOL.name);
        const input = args as unknown as ReplaceSelectedTextInput;
        const replaced = this.copilotContext.executeAction(REPLACE_SELECTED_TEXT_TOOL.name, input);
        if (!replaced) {
          throw new Error(
            `${REPLACE_SELECTED_TEXT_TOOL.name}: the knowledge-article-edit handler couldn't apply this — the editor or selection range isn't ready. Make sure the staff member has text selected.`,
          );
        }

        return { content: [{ type: 'text', text: JSON.stringify({ replaced: true }) }] };
      }),
    });

    ctx.registerTool({
      name: INSERT_TEXT_AT_CURSOR_TOOL.name,
      description: INSERT_TEXT_AT_CURSOR_TOOL.description,
      inputSchema: INSERT_TEXT_AT_CURSOR_TOOL.inputSchema as never,
      execute: this.instrument(INSERT_TEXT_AT_CURSOR_TOOL.name, async (args) => {
        this.requirePage('knowledge-article-edit', INSERT_TEXT_AT_CURSOR_TOOL.name);
        const input = args as unknown as InsertTextAtCursorInput;
        const inserted = this.copilotContext.executeAction(INSERT_TEXT_AT_CURSOR_TOOL.name, input);
        if (!inserted) {
          throw new Error(
            `${INSERT_TEXT_AT_CURSOR_TOOL.name}: the knowledge-article-edit handler couldn't apply this — the editor or cursor position isn't ready, the article changed, or there are outstanding diffs.`,
          );
        }

        return { content: [{ type: 'text', text: JSON.stringify({ inserted: true }) }] };
      }),
    });

    // Shape 2: app-wide primitive. Action handler is registered on
    // AppComponent (`createKbArticleDraft`) so it's available regardless of
    // which page the staff member is currently on. The BE Copilot emits the
    // same `invoke-fe-tool` event with `name: 'create_kb_article_draft'`.
    ctx.registerTool({
      name: CREATE_KB_ARTICLE_DRAFT_TOOL.name,
      description: CREATE_KB_ARTICLE_DRAFT_TOOL.description,
      inputSchema: CREATE_KB_ARTICLE_DRAFT_TOOL.inputSchema as never,
      execute: this.instrument(CREATE_KB_ARTICLE_DRAFT_TOOL.name, async (args) => {
        const input = args as unknown as CreateKbArticleDraftInput;
        const created = await this.copilotContext.executeActionAsync(CREATE_KB_ARTICLE_DRAFT_TOOL.name, input);
        if (!created) {
          throw new Error(
            `${CREATE_KB_ARTICLE_DRAFT_TOOL.name}: the app-wide handler couldn't open the draft — either no articleId was provided and title/content were empty, or article creation/navigation failed.`,
          );
        }

        return { content: [{ type: 'text', text: JSON.stringify({ created: true }) }] };
      }),
    });

    // Shape 2: page-bound primitive. The knowledge-article-edit provider's
    // `apply_kb_article_diff` handler renders the diff inside the editor for
    // the staff member to accept or reject. The BE Copilot's
    // `propose_kb_article_edit` tool emits the same shape.
    ctx.registerTool({
      name: APPLY_KB_ARTICLE_DIFF_TOOL.name,
      description: APPLY_KB_ARTICLE_DIFF_TOOL.description,
      inputSchema: APPLY_KB_ARTICLE_DIFF_TOOL.inputSchema as never,
      execute: this.instrument(APPLY_KB_ARTICLE_DIFF_TOOL.name, async (args) => {
        this.requirePage('knowledge-article-edit', APPLY_KB_ARTICLE_DIFF_TOOL.name);
        const input = args as unknown as ApplyKbArticleDiffInput;
        const applied = this.copilotContext.executeAction(APPLY_KB_ARTICLE_DIFF_TOOL.name, input);
        if (!applied) {
          throw new Error(
            `${APPLY_KB_ARTICLE_DIFF_TOOL.name}: the knowledge-article-edit handler couldn't apply this — the editor isn't ready, or there are outstanding diffs the user hasn't yet accepted/rejected.`,
          );
        }

        return { content: [{ type: 'text', text: JSON.stringify({ applied: true }) }] };
      }),
    });

    // Shape 3a: sub-agent wrapper. Execute body POSTs the user's intent to a
    // thin BE endpoint that runs the navigation sub-agent (entity resolution +
    // page-catalog URL construction), then applies the resolved URL via the
    // app-wide `navigate` action.
    ctx.registerTool({
      name: WebMcpService.NAVIGATION_ASSISTANT_TOOL_NAME,
      description:
        "Navigate the dashboard based on a natural-language request. Pass the user's message verbatim. The assistant handles entity resolution (project/user/customer/automation/article names → ids) and page-catalog URL construction, then applies the navigation. Returns the resolved URL and a short description of where the user was taken.",
      inputSchema: {
        type: 'object',
        properties: {
          instruction: {
            type: 'string',
            description: 'Natural-language navigation request (e.g. "take me to the HR project settings").',
          },
        },
        required: ['instruction'],
      },
      execute: this.instrument(WebMcpService.NAVIGATION_ASSISTANT_TOOL_NAME, async (args) => {
        const { instruction } = args as { instruction: string };

        const result = await this.navigationAssistant.resolve({
          message: instruction,
          context: { currentUrl: window.location.href },
        });

        const applied = result.url
          ? this.copilotContext.executeAction(NAVIGATE_ACTION_NAME, { url: result.url, pageId: result.pageId })
          : false;

        return {
          content: [
            {
              type: 'text',
              text: JSON.stringify({ applied, ...result }),
            },
          ],
        };
      }),
    });

    // Shape 3a: sub-agent wrapper. Execute body POSTs the user's intent + the
    // active inbox page's filter context to a thin BE endpoint that runs the
    // filter sub-agent (entity resolution + clause construction), then applies
    // the resolved clauses via the inbox provider's `apply_inbox_filters`
    // action.
    ctx.registerTool({
      name: WebMcpService.FILTER_ASSISTANT_TOOL_NAME,
      description:
        "Filter the inbox based on a natural-language request. Pass the user's message verbatim. The assistant handles entity resolution (assignee/tag/project/etc. names → ids), maps to the right filter field, and applies the resolved filter set — replacing any current filters. Only valid on the inbox page.",
      inputSchema: {
        type: 'object',
        properties: {
          instruction: {
            type: 'string',
            description:
              'Natural-language filter request (e.g. "show open tickets assigned to me with the billing tag").',
          },
        },
        required: ['instruction'],
      },
      execute: this.instrument(WebMcpService.FILTER_ASSISTANT_TOOL_NAME, async (args) => {
        const { instruction } = args as { instruction: string };
        const inboxContext = this.requirePage('inbox', WebMcpService.FILTER_ASSISTANT_TOOL_NAME);

        // Whether the most recent `apply_inbox_filters` dispatch actually
        // landed (inbox provider active, handler returned true). Captured
        // inside `onFilterChange` so the WebMCP return reflects reality.
        let applied = false;

        // FilterAssistantService streams via SSE; wrap that into a single promise
        // so the WebMCP callTool() return arrives only once the sub-agent has
        // resolved final filters (or errored). Intermediate `onFilterChange`
        // applies them eagerly so the user sees the result as soon as it's ready.
        const result = await new Promise<{
          filters?: WhereClause[];
          resolvedEntityLabels?: Record<string, string>;
          message: string;
        }>((resolve, reject) => {
          // `onError` only fires for in-stream error events surfaced by the
          // SSE handler. Transport-level failures (network drop, 4xx/5xx,
          // abort) reject `sendMessage`'s own promise — chain `.catch` so the
          // outer Promise doesn't hang forever.
          this.filterAssistant
            .sendMessage(
              {
                message: instruction,
                context: {
                  availableFilters: inboxContext.availableFilters,
                  currentFilters: inboxContext.appliedFilters,
                },
                // Forward projectId so entity lookup + filter resolution stay
                // scoped to the project on `/dashboard/projects/:projectId/inbox/...`.
                // The inbox filter UI passes this through; we should match.
                projectId: inboxContext.projectId,
              },
              {
                onFilterChange: (filters, resolvedEntityLabels) => {
                  applied = this.copilotContext.executeAction(APPLY_INBOX_FILTERS_TOOL.name, {
                    filters,
                    resolvedEntityLabels,
                  });
                },
                onDone: (response) => {
                  // The done event may carry the final filters even when no
                  // intermediate filter-change was emitted (e.g. when the
                  // assistant resolves entities in a single shot). Mirror the
                  // inbox UI, which applies in both places. Skip if onFilterChange
                  // already applied so we don't double-dispatch the same filters.
                  if (!applied && response.filters) {
                    applied = this.copilotContext.executeAction(APPLY_INBOX_FILTERS_TOOL.name, {
                      filters: response.filters,
                      resolvedEntityLabels: response.resolvedEntityLabels,
                    });
                  }
                  resolve(response);
                },
                onError: (error) => reject(new Error(`filter_assistant: ${error}`)),
              },
            )
            .catch(reject);
        });

        return {
          content: [
            {
              type: 'text',
              text: JSON.stringify({ applied, ...result }),
            },
          ],
        };
      }),
    });
  }

  /**
   * Wraps a tool's execute callback in success/error tracking. Emits Mixpanel
   * events under the `webmcp` category via the existing AnalyticsService, so
   * usage shows up in the same channel as the rest of frontend product analytics.
   */
  private instrument<TArgs, TResult>(
    name: string,
    fn: (args: TArgs, client: unknown) => Promise<TResult>,
  ): (args: TArgs, client: unknown) => Promise<TResult> {
    return async (args, client) => {
      const start = performance.now();

      try {
        const result = await fn(args, client);

        this.analytics.trackEvent('webmcp', 'tool_success', {
          tool: name,
          ms: Math.round(performance.now() - start),
        });

        return result;
      } catch (err) {
        this.analytics.trackEvent('webmcp', 'tool_error', {
          tool: name,
          ms: Math.round(performance.now() - start),
          error: (err as Error)?.message ?? String(err),
        });

        throw err;
      }
    };
  }
}
